import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  useTheme,
} from '@mui/material'
import {
  Assignment,
  Groups,
  EditNote,
} from '@mui/icons-material'
// import AttendanceChart from '../components/AttendanceChart'
import ClassStudentAttendanceChart from '../components/ClassStudentAttendanceChart'
import ClassStudentQuranProgressChart from '../components/ClassStudentQuranProgressChart'
import QuranProgressLineChart from '../components/QuranProgressLineChart'
import ProgressModeToggle from '../components/ui/ProgressModeToggle'
import { getTeacherAccessibleClasses, 
  // getClassDisplayName 
} from '../utils/classUtils'
import {
  PageContainer,
  SectionHeader,
  StyledCard,
} from '../components/ui/StyledComponents'
import SectionDivider from '../components/ui/SectionDivider'

const MyStatsPage = () => {
  const { teacherId } = useParams()
  const navigate = useNavigate()
  const theme = useTheme()
  const [loading, setLoading] = useState(true)
  const [selectedClassId, setSelectedClassId] = useState('all')
  const [progressMode, setProgressMode] = useState('verses')
  const [classes, setClasses] = useState([])

  useEffect(() => {
    const fetchTeacherClasses = async () => {
      try {
        // Fetch classes accessible to this teacher (regular + substitute)
        const data = await getTeacherAccessibleClasses(teacherId, 'id, name');
        
        if (data && data.length > 0) {
          // Store all classes and set 'all' as the default selection
          setClasses(data);
          setSelectedClassId('all');
        }
        setLoading(false);
      } catch (error) {
        console.error('Error in fetchTeacherClasses:', error);
        setLoading(false);
      }
    }

    if (teacherId) {
      fetchTeacherClasses()
    }
  }, [teacherId])

  const handleClassChange = (event) => {
    setSelectedClassId(event.target.value)
  }

  if (loading) {
    return (
      <PageContainer>
        <Typography variant="h6">Loading dashboard...</Typography>
      </PageContainer>
    )
  }

  // Navigation card data
  const navigationCards = [
    {
      title: 'Record Attendance & Progress',
      subtitle: 'Quick Actions',
      description: 'Take attendance and record student progress',
      icon: Groups,
      path: `/teacher/${teacherId}/attendance`,
      color: '#3498db',
      rgbColor: '52, 152, 219'
    },
    {
      title: 'Take Progress Notes',
      subtitle: 'Student Notes',
      description: 'Add notes and comments about student progress',
      icon: EditNote,
      path: `/student-notes`,
      color: '#27ae60',
      rgbColor: '39, 174, 96'
    },
    {
      title: 'Request Coverage',
      subtitle: 'Coverage Management',
      description: 'View and manage substitute assignments',
      icon: Assignment,
      path: `/teacher/${teacherId}/substitute-assignments`,
      color: '#e67e22',
      rgbColor: '230, 126, 34'
    }
  ]

  const handleCardClick = (path) => {
    navigate(path)
  }

  return (
    <PageContainer>
      <SectionHeader sx={{ 
        mb: { xs: 2, sm: 3 },
        fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
      }}>
        My Dashboard
      </SectionHeader>

      {/* Quick Actions Cards */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 3, sm: 4 } }}>
        {navigationCards.map((card, index) => {
          const IconComponent = card.icon
          return (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <StyledCard
                onClick={() => handleCardClick(card.path)}
                sx={{
                  cursor: 'pointer',
                  position: 'relative',
                  background: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  transform: 'translateY(0)',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.palette.mode === 'dark' 
                      ? '0 12px 40px rgba(0, 0, 0, 0.5)' 
                      : '0 12px 40px rgba(0, 0, 0, 0.15)',
                    borderColor: card.color,
                  },
                }}
              >
                <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1.5, sm: 2 } }}>
                    <Box
                      sx={{
                        width: { xs: 40, sm: 44, md: 48 },
                        height: { xs: 40, sm: 44, md: 48 },
                        borderRadius: 2,
                        backgroundColor: theme.palette.mode === 'dark' 
                          ? `rgba(${card.rgbColor}, 0.2)` 
                          : `rgba(${card.rgbColor}, 0.1)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: { xs: 1.5, sm: 2 },
                        flexShrink: 0,
                      }}
                    >
                      <IconComponent sx={{ fontSize: { xs: 20, sm: 22, md: 24 }, color: card.color }} />
                    </Box>
                    <Box>
                      <Typography variant="h6" component="h2" sx={{ 
                        fontWeight: 600, 
                        color: theme.palette.text.primary, 
                        fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                      }}>
                        {card.title}
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        color: theme.palette.text.secondary, 
                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                      }}>
                        {card.subtitle}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ 
                    color: theme.palette.text.secondary, 
                    fontSize: { xs: '0.75rem', sm: '0.8rem' }, 
                    lineHeight: 1.4,
                  }}>
                    {card.description}
                  </Typography>
                </Box>
              </StyledCard>
            </Grid>
          )
        })}
      </Grid>
      {/* Section Divider */}
      <SectionDivider />
      
      {/* Analytics Section */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        mb: 3,
        gap: { xs: 2, sm: 0 },
      }}>
        <Typography 
          variant="h5" 
          sx={{ 
            mb: 0,
            fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
          }}
        >
          Analytics
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 2 },
          alignItems: { xs: 'stretch', sm: 'center' },
          width: { xs: '100%', sm: 'auto' },
        }}>
          {classes.length > 0 && (
            <FormControl sx={{ minWidth: { xs: '100%', sm: 200 }, width: { xs: '100%', sm: 'auto' } }}>
              <InputLabel id="class-select-label">Filter by Class</InputLabel>
              <Select
                labelId="class-select-label"
                id="class-select"
                value={selectedClassId || 'all'}
                label="Filter by Class"
                onChange={handleClassChange}
                size="small"
              >
                <MenuItem value="all">All Classes</MenuItem>
                {classes.map((cls) => (
                  <MenuItem key={cls.id} value={cls.id}>
                    {cls.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <ProgressModeToggle value={progressMode} onChange={(event, newValue) => {
            if (newValue !== null) {
              setProgressMode(newValue);
            }
          }} />
        </Box>
      </Box>

      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {/* Student Attendance by Class Card */}
        <Grid item xs={12} md={6}>
          <StyledCard sx={{
            borderRadius: 3,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: theme.palette.mode === 'dark' 
                ? '0 12px 40px rgba(0, 0, 0, 0.5)' 
                : '0 12px 40px rgba(0, 0, 0, 0.15)',
            },
          }}>
            <Typography variant="h6" component="h3" sx={{ 
              mb: { xs: 1.5, sm: 2 },
              fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
            }}>
              Attendance Overview By Student
            </Typography>

            {classes.length > 0 ? (
              <ClassStudentAttendanceChart
                classId={selectedClassId}
                teacherId={teacherId}
              />
            ) : (
              <Typography variant="body1" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                No classes found for this teacher
              </Typography>
            )}
          </StyledCard>
        </Grid>

        {/* Quran Progress Card */}
        <Grid item xs={12} md={6}>
          <StyledCard sx={{
            borderRadius: 3,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: theme.palette.mode === 'dark' 
                ? '0 12px 40px rgba(0, 0, 0, 0.5)' 
                : '0 12px 40px rgba(0, 0, 0, 0.15)',
            },
          }}>
            <Typography variant="h6" component="h3" sx={{ 
              mb: { xs: 1.5, sm: 2 },
              fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
            }}>
              Quran Progress Overview By Student
            </Typography>

            {classes.length > 0 ? (
              <ClassStudentQuranProgressChart
                classId={selectedClassId}
                teacherId={teacherId}
                progressMode={progressMode}
              />
            ) : (
              <Typography variant="body1" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                No classes found for this teacher
              </Typography>
            )}
          </StyledCard>
        </Grid>

        {/* Quran Progress Line Chart */}
        <Grid item xs={12}>
          <StyledCard sx={{
            borderRadius: 3,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: theme.palette.mode === 'dark' 
                ? '0 12px 40px rgba(0, 0, 0, 0.5)' 
                : '0 12px 40px rgba(0, 0, 0, 0.15)',
            },
          }}>
            <Typography variant="h6" component="h3" sx={{ 
              mb: { xs: 1.5, sm: 2 },
              fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
            }}>
              Quran Progress Trends
            </Typography>

            {classes.length > 0 ? (
              <QuranProgressLineChart
                classId={selectedClassId}
                teacherId={teacherId}
                progressMode={progressMode}
              />
            ) : (
              <Typography variant="body1" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                No classes found for this teacher
              </Typography>
            )}
          </StyledCard>
        </Grid>
      </Grid>
    </PageContainer>
  )
}

export default MyStatsPage
